<template>
  <div class="full-height-messaging-view w-100pc bg-super-light-blue">
    <v-row
      class="w-100pc h-100pc bg-white flex-grow-1"
      no-gutters
    >
      <v-col
        v-show="display.mdAndUp || activeThread == false"
        class="br-1 bc-extra-light-gray"
        cols="12"
        md="4"
      >
        <h1
          ref="title"
          class="bb-1 bc-extra-light-gray pb-2 fs-24 fw-600 mt-2"
          tabindex="0"
        >
          <span class="ml-4">{{ $bct('My messages') }}</span>
        </h1>
        <div class="pa-4 bb-1 bc-extra-light-gray d-flex align-center">
          <v-text-field
            v-model="query"
            :label="$bct('Search by provider name')"
            data-cy="search_provider"
            density="comfortable"
            prepend-inner-icon="search"
            variant="solo-filled"
            flat
            hide-details
            rounded
          />
          <v-btn
            @click="load"
            :loading="processing"
            class="ms-1"
            icon="refresh"
            variant="text"
          />
        </div>
        <div
          v-if="store.state.pages.Messaging.features.enable_support"
          @click="switchThread(null)"
          class="pa-4 bb-1 bc-extra-light-gray d-flex align-center pointer"
          data-cy="support-thread"
          data-testid="support-thread"
        >
          <v-icon
            class="me-2"
            icon="support_agent"
            size="26"
          />
          <div>
            <div class="fs-18 fw-500">
              {{ store.state.brand.organization_name }}
            </div>
            <div class="fs-16 c-light-black">
              {{ $bct('Need assistance? Message one of our staff members.') }}
            </div>
          </div>
          <v-spacer />
          <v-icon
            v-if="
              messagesWithOrg.length > 0 &&
              [messagesWithOrg[messagesWithOrg.length - 1]].some(
                (message) =>
                  (message.meta.member.is_provider || message.meta.member.is_specialist) &&
                  !message.read_at,
              )
            "
            class="mx-1"
            color="green"
            icon="circle"
          />
        </div>
        <div
          v-for="provider in filteredProviderThreads"
          @click="switchThread(provider)"
          :key="provider.id"
          class="pa-4 bb-1 bc-extra-light-gray d-flex align-center pointer"
          data-cy="provider-thread"
        >
          <v-icon
            class="me-2"
            icon="storefront"
            size="26"
          />

          <div>
            <div class="fs-18 fw-500">
              {{ provider.name }}
            </div>
            <MarkdownContent
              :content="
                messagesWithProviders[provider.id][messagesWithProviders[provider.id].length - 1]
                  ?.text
              "
              class="fs-16 c-light-black h-20 oy-hidden"
              no-translate
            />
          </div>
          <v-spacer />
          <v-icon
            v-if="
              [
                messagesWithProviders[provider.id][messagesWithProviders[provider.id].length - 1],
              ].some(
                (message) =>
                  (message.meta.member.is_provider || message.meta.member.is_specialist) &&
                  !message.read_at,
              )
            "
            class="mx-1"
            color="green"
            icon="circle"
          />
        </div>
      </v-col>

      <v-col
        v-show="display.mdAndUp || activeThread != false"
        class="p-relative d-flex flex-column h-100pc"
        cols="12"
        data-cy="conversation"
        md="8"
      >
        <div class="d-flex flex-column h-100pc flex-grow-1">
          <MessageThreadHeader
            @back="switchThread(false)"
            :subtitle="messageHeaderSubtitle"
            :title="messageHeaderTitle"
          />

          <div
            v-if="store.state.pages.Messaging.features.enable_support && activeThread == null"
            id="messages_list"
            :class="messageListClass"
          >
            <MessageItem
              v-for="message in messagesWithOrg"
              :key="message.id"
              :message="message"
            />
          </div>

          <div
            v-else
            id="messages_list"
            :class="messageListClass"
          >
            <div
              v-for="provider in filteredProviderThreads"
              :key="provider.id"
            >
              <div v-if="activeThread && activeThread.id === provider.id">
                <MessageItem
                  v-for="message in messagesWithProviders[provider.id]"
                  :key="message.id"
                  :message="message"
                />
              </div>
            </div>
          </div>

          <div
            v-if="activeThread || store.state.pages.Messaging.features.enable_support"
            class="bt-0 bc-extra-light-gray bg-white w-100pc"
            data-cy="message-composer"
          >
            <div class="pa-2">
              <v-row dense>
                <v-col>
                  <v-card
                    class="bb-1 bc-very-light-grey"
                    style="background: rgba(0, 0, 0, 0.04)"
                    flat
                    tile
                  >
                    <v-btn
                      @click="$refs.attachmentDialog.open({})"
                      class="mb-2"
                      data-cy="attach-button"
                      prepend-icon="attachment"
                      variant="flat"
                    >
                      {{ $bct('Attach document') }}
                    </v-btn>
                  </v-card>
                </v-col>
              </v-row>
              <AttachmentDialog
                @save="createAttachmentMessages"
                @upload="uploadedFiles.push($event)"
                ref="attachmentDialog"
                :processing="processing"
              />
              <v-textarea
                v-model="newMessageText"
                :aria-label="$bct('Type message')"
                :disabled="noMessages"
                :label="$bct('Message')"
                :rows="display.smAndDown.value ? 1 : 3"
                class="mb-2"
                data-cy="message-textarea"
                variant="filled"
                hide-details
              />
              <v-btn
                @click="createMessage"
                :disabled="submitDisabled"
                :loading="processing"
                color="primary"
                data-cy="send-message-button"
                size="x-large"
                block
              >
                {{ $bct('Send message') }}
              </v-btn>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script setup>
import AttachmentDialog from '@/shared/components/attachments/AttachmentDialog.vue';
import MarkdownContent from '@/shared/components/MarkdownContent.vue';
import MessageItem from '@/shared/components/MessageItem.vue';
import MessageThreadHeader from '@/shared/components/MessageThreadHeader.vue';
import useApi from '@/shared/composables/useApi';
import { useDisplay } from 'vuetify';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';

const { api } = useApi();
const display = useDisplay();
const store = useStore();
const route = useRoute();
const $bct = inject('translator');

const activeThread = ref(display.mdAndUp.value ? null : false);
const messages = ref([]);
const messagesWithOrg = ref([]);
const messagesWithProviders = ref({});
const newMessageText = ref(null);
const processing = ref(false);
const providerThreads = ref([]);
const query = ref(null);
const uploadedFiles = ref([]);

// Template refs
const attachmentDialog = ref(null);
const title = ref(null);

const filteredProviderThreads = computed(() => {
  if (query.value && query.value !== '') {
    return providerThreads.value.filter((thread) =>
      thread.name.toLowerCase().includes(query.value.toLowerCase()),
    );
  }
  return providerThreads.value;
});

const messageHeaderSubtitle = computed(() => {
  if (activeThread.value) {
    return activeThread.value.address;
  }
  if (store.state.pages.Messaging.features.enable_support) {
    return 'Support staff will message you on this channel.';
  }
  return 'Messages will appear here when you send or receive a message.';
});

const messageHeaderTitle = computed(() => {
  if (activeThread.value) {
    return activeThread.value.name;
  }
  if (store.state.pages.Messaging.features.enable_support) {
    return store.state.brand.organization_name;
  }
  return 'No messages yet!';
});

const noMessages = computed(() => {
  return activeThread.value == null && !store.state.pages.Messaging.features.enable_support;
});

const submitDisabled = computed(() => {
  return !newMessageText.value || newMessageText.value.trim().length === 0 || noMessages.value;
});

const messageListClass = computed(() => [
  'd-flex flex-column flex-1-1-0 min-h-0 oy-auto w-100 pa-4 bg-white',
  'message-list-scrollable',
  'flex-grow-1',
]);

function createMessage() {
  processing.value = true;
  api.message.create(
    { text: newMessageText.value, provider_id: activeThread.value ? activeThread.value.id : null },
    (resp) => {
      messages.value.push(resp.data);
      processing.value = false;
      newMessageText.value = null;
      sortMessages();
      scrollToBottom();
    },
  );
}

async function createAttachmentMessages() {
  processing.value = true;

  const params = { provider_id: activeThread.value ? activeThread.value.id : null };

  const responses = await Promise.all(
    uploadedFiles.value.map(async (asset) => {
      const attachmentResponse = await api.member.attachment.create(asset);
      if (attachmentResponse?.status !== 201) {
        return;
      }
      const messageResponse = await api.message.promiseCreate({
        ...params,
        attachment_id: attachmentResponse.data.id,
      });
      messages.value.push(messageResponse.data);
      return messageResponse;
    }),
  );

  const allSuccessful = responses.every((resp) => resp?.status === 201);
  if (!allSuccessful) {
    attachmentDialog.value.cancelAll();
  } else {
    attachmentDialog.value.close();
  }

  uploadedFiles.value = [];
  processing.value = false;
  newMessageText.value = null;
  sortMessages();
  scrollToBottom();
}

function load() {
  messages.value = [];
  messagesWithOrg.value = [];
  messagesWithProviders.value = {};
  providerThreads.value = [];

  processing.value = true;
  api.message.index({}, (resp) => {
    messages.value = resp.data;
    processing.value = false;
    sortMessages();
    scrollToBottom();
  });
}

function scrollToBottom() {
  nextTick(() => {
    const list = document.getElementById('messages_list');
    if (list) {
      list.scrollTop = list.scrollHeight;
    }
  });
}

function sortMessages() {
  messages.value.forEach((message) => {
    if (message.provider_id == null) {
      if (!messagesWithOrg.value.includes(message)) {
        messagesWithOrg.value.push(message);
      }
    } else {
      if (messagesWithProviders.value[message.provider_id] === undefined) {
        messagesWithProviders.value[message.provider_id] = [];
        if (!providerThreads.value.includes(message.meta.provider)) {
          providerThreads.value.push(message.meta.provider);
        }
      }

      if (!messagesWithProviders.value[message.provider_id].includes(message)) {
        messagesWithProviders.value[message.provider_id].push(message);
      }
    }
  });

  if (activeThread.value) {
    switchThread(
      filteredProviderThreads.value.find((thread) => thread.id === activeThread.value.id),
    );
  } else if (route.query.provider_id) {
    switchThread(
      filteredProviderThreads.value.find((thread) => thread.id === route.query.provider_id),
    );
  } else if (!store.state.pages.Messaging.features.enable_support && activeThread.value == null) {
    switchThread(providerThreads.value[0]);
  }
}

function switchThread(thread) {
  activeThread.value = thread;
  if (thread !== false) {
    scrollToBottom();
    let msg;
    if (thread && thread.id) {
      msg =
        messagesWithProviders.value[activeThread.value.id][
          messagesWithProviders.value[activeThread.value.id].length - 1
        ];
    } else {
      msg = messagesWithOrg.value[messagesWithOrg.value.length - 1];
    }
    if (msg) {
      if (!msg.read_at) {
        api.message.get(msg.id, () => {});
        msg.read_at = true;
      }
    }
  }
}

onMounted(() => {
  store.commit('setNewMessage', false);
  load();
  title.value?.focus();
});
</script>

<style scoped>
/* Set top level container to full height
   Without this, mobile UI will be pushed by other elements like banners
*/
.full-height-messaging-view {
  height: 100vh;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: white;
}

[data-cy='conversation'] {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

.message-list-scrollable {
  flex: 1 1 auto;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

[data-cy='message-composer'] {
  flex-shrink: 0;
  width: 100%;
  border-top: 1px solid #e0e0e0;
}

#messages_list {
  word-wrap: break-word;
  overflow-wrap: break-word;
}

#messages_list > * {
  margin-bottom: 8px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
}
#messages_list > *:last-child {
  margin-bottom: 0;
}
</style>
